import {
  Directive,
  ElementRef,
  Input,
  Renderer2,
  OnDestroy,
  ComponentRef,
  ApplicationRef,
  Injector,
  createComponent,
  EnvironmentInjector,
  AfterViewInit,
  OnInit
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { TourGuideTooltipComponent } from '../components/tour-guide-tooltip/tour-guide-tooltip.component';
import { TourGuideTooltipService } from '../services/tour-guide-tooltip.service';
import { TooltipConfig } from '../models/tooltip.model';
import { TooltipPosition, TooltipAlignment } from '../models/tooltip.enum';
import { TooltipInjectorService } from '../services/tooltip-injector.service';
import { TooltipZIndexService } from '../services/tooltip-utils.service';

@Directive({
  selector: '[appManagedTooltip]',
  standalone: false
})
export class ManagedTooltipDirective implements OnInit, AfterViewInit, OnDestroy {
  @Input() tooltipSelector?: string; // Optional: manually specify selector

  private overlayElement: HTMLElement | null = null;
  private tooltipComponentRef: ComponentRef<TourGuideTooltipComponent> | null = null;
  private questionMarkIcon: HTMLElement | null = null;
  private isTooltipVisible: boolean = false;
  private tooltipConfig: TooltipConfig | null = null;
  private destroy$ = new Subject<void>();
  private readonly iconSize = 20;
  private readonly iconGap = 8;
  private intersectionObserver: IntersectionObserver | null = null;
  private scrollListener: (() => void) | null = null;

  constructor(
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private appRef: ApplicationRef,
    private injector: Injector,
    private environmentInjector: EnvironmentInjector,
    private tooltipService: TourGuideTooltipService,
    private injectorService: TooltipInjectorService,
    private zIndex: TooltipZIndexService
  ) {}

  ngOnInit(): void {
    this.tooltipService.tooltips$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.checkAndUpdateTooltip());
  }

  ngAfterViewInit(): void {
    this.checkAndUpdateTooltip();
  }

  private checkAndUpdateTooltip(): void {
    const tooltipId = this.elementRef.nativeElement.getAttribute('data-tooltip-id');
    if (!tooltipId) return;
    const config = this.tooltipService.getTooltipById(tooltipId);
    if (config) this.injectorService.injectIfMarked(tooltipId);
  }

  private createQuestionMarkIcon(): void {
    if (this.questionMarkIcon) return;
    if (!this.tooltipConfig) return;

    this.questionMarkIcon = this.renderer.createElement('span');
    this.renderer.addClass(this.questionMarkIcon, 'tour-guide-question-icon');
    this.renderer.setProperty(this.questionMarkIcon, 'innerHTML', '?');

    this.positionQuestionMarkIcon();
    this.renderer.setStyle(this.questionMarkIcon, 'z-index', String(this.zIndex.QUESTION_MARK));

    this.renderer.listen(this.questionMarkIcon, 'click', (event: Event) => {
      event.stopPropagation();
      this.toggleTooltip();
    });

    const computedStyle = window.getComputedStyle(this.elementRef.nativeElement);
    if (computedStyle.position === 'static') {
      this.renderer.setStyle(this.elementRef.nativeElement, 'position', 'relative');
    }

    this.renderer.appendChild(this.elementRef.nativeElement, this.questionMarkIcon);
  }

  private positionQuestionMarkIcon(): void {
    if (!this.questionMarkIcon || !this.tooltipConfig) return;

    const placement = this.tooltipConfig.iconPlacement ?? TooltipPosition.Right;
    const align = this.tooltipConfig.iconAlign ?? TooltipAlignment.Center;
    const rotation = (this.tooltipConfig.iconRotation ?? 0) % 360;

    const elementRect = this.elementRef.nativeElement.getBoundingClientRect();
    let top = 0;
    let left = 0;

    const alignVert = () => {
      if (align === TooltipAlignment.Top) return 0;
      if (align === TooltipAlignment.Bottom) return elementRect.height - this.iconSize;
      return (elementRect.height - this.iconSize) / 2;
    };

    const alignHorz = () => {
      if (align === TooltipAlignment.Left) return 0;
      if (align === TooltipAlignment.Right) return elementRect.width - this.iconSize;
      return (elementRect.width - this.iconSize) / 2;
    };

    switch (placement) {
      case TooltipPosition.Left:
        left = -this.iconSize - this.iconGap;
        top = alignVert();
        break;
      case TooltipPosition.Top:
        top = -this.iconSize - this.iconGap;
        left = alignHorz();
        break;
      case TooltipPosition.Bottom:
        top = elementRect.height + this.iconGap;
        left = alignHorz();
        break;
      case TooltipPosition.Right:
      default:
        left = elementRect.width + this.iconGap;
        top = alignVert();
        break;
    }

    top += this.tooltipConfig.iconOffsetTop ?? 0;
    left += this.tooltipConfig.iconOffsetLeft ?? 0;

    this.renderer.setStyle(this.questionMarkIcon, 'position', 'absolute');
    this.renderer.setStyle(this.questionMarkIcon, 'top', `${top}px`);
    this.renderer.setStyle(this.questionMarkIcon, 'left', `${left}px`);
    // Set CSS custom property for rotation (used by CSS hover/active states)
    this.questionMarkIcon.style.setProperty('--tour-guide-icon-rotate', `${rotation}deg`);
  }

  private removeQuestionMarkIcon(): void {
    if (this.questionMarkIcon) {
      this.renderer.removeChild(
        this.questionMarkIcon.parentNode,
        this.questionMarkIcon
      );
      this.questionMarkIcon = null;
    }
  }

  private toggleTooltip(): void {
    if (this.isTooltipVisible) {
      this.hideTooltip();
    } else {
      this.showTooltip();
    }
  }

  private showTooltip(): void {
    if (!this.tooltipConfig || this.isTooltipVisible) return;

    try {
      this.isTooltipVisible = true;
      this.createOverlay();
      this.highlightElement();
      this.createTooltipComponent();

      this.scrollListener = this.renderer.listen('window', 'scroll', () => {
        this.repositionTooltip();
      });

      const options = { root: null, threshold: 0 };
      this.intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting) {
            this.hideTooltip();
          }
        });
      }, options);
      this.intersectionObserver.observe(this.elementRef.nativeElement);
    } catch (e) {
      console.error('Error showing tooltip:', e);
      this.hideTooltip();
    }
  }

  private repositionTooltip(): void {
    if (this.tooltipComponentRef) {
        const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;
        this.positionTooltip(domElem);
    }
  }

  private createOverlay(): void {
    this.overlayElement = this.renderer.createElement('div');
    this.renderer.addClass(this.overlayElement, 'tour-guide-overlay');
    this.renderer.appendChild(document.body, this.overlayElement);

    this.renderer.listen(this.overlayElement, 'click', () => {
      this.hideTooltip();
    });
  }

  private highlightElement(): void {
    const element = this.elementRef.nativeElement;
    this.renderer.addClass(element, 'tour-guide-highlighted');
    this.renderer.setStyle(element, 'position', 'relative');
    this.renderer.setStyle(element, 'z-index', String(this.zIndex.HIGHLIGHTED_ELEMENT));
  }

  private createTooltipComponent(): void {
    if (!this.tooltipConfig) return;

    this.tooltipComponentRef = createComponent(TourGuideTooltipComponent, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector
    });

    // Set component inputs from config
    this.tooltipComponentRef.instance.text = this.tooltipConfig.text;
    this.tooltipComponentRef.instance.header = this.tooltipConfig.header;
    this.tooltipComponentRef.instance.position = this.tooltipConfig.position;

    this.tooltipComponentRef.instance.closeTooltip.subscribe(() => {
      this.hideTooltip();
    });

    this.appRef.attachView(this.tooltipComponentRef.hostView);
    this.tooltipComponentRef.changeDetectorRef.detectChanges();

    const domElem = (this.tooltipComponentRef.hostView as any).rootNodes[0] as HTMLElement;
    this.renderer.appendChild(document.body, domElem);

    setTimeout(() => {
      this.positionTooltip(domElem);
    }, 0);
  }

  private positionTooltip(tooltipElement: HTMLElement): void {
    if (!tooltipElement || !this.tooltipConfig) return;

    const targetRect = this.elementRef.nativeElement.getBoundingClientRect();
    const tooltipRect = tooltipElement.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gap = 20;
    const padding = 10; // Padding from viewport edge

    let top = 0;
    let left = 0;
    let finalPosition: TooltipPosition = this.tooltipConfig.position;

    switch (this.tooltipConfig.position) {
      case TooltipPosition.Top:
        top = targetRect.top - tooltipRect.height - gap;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        if (top < padding) { finalPosition = TooltipPosition.Bottom; top = targetRect.bottom + gap; }
        break;
      case TooltipPosition.Bottom:
        top = targetRect.bottom + gap;
        left = targetRect.left + (targetRect.width - tooltipRect.width) / 2;
        if (top + tooltipRect.height > viewportHeight - padding) { finalPosition = TooltipPosition.Top; top = targetRect.top - tooltipRect.height - gap; }
        break;
      case TooltipPosition.Left:
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.left - tooltipRect.width - gap;
        if (left < padding) { finalPosition = TooltipPosition.Right; left = targetRect.right + gap; }
        break;
      case TooltipPosition.Right:
      default:
        top = targetRect.top + (targetRect.height - tooltipRect.height) / 2;
        left = targetRect.right + gap;
        if (left + tooltipRect.width > viewportWidth - padding) { finalPosition = TooltipPosition.Left; left = targetRect.left - tooltipRect.width - gap; }
        break;
    }

    // Ensure tooltip stays within viewport bounds horizontally
    if (left < padding) {
      left = padding;
    } else if (left + tooltipRect.width > viewportWidth - padding) {
      left = viewportWidth - tooltipRect.width - padding;
    }

    // Ensure tooltip stays within viewport bounds vertically
    if (top < padding) {
      top = padding;
    } else if (top + tooltipRect.height > viewportHeight - padding) {
      top = viewportHeight - tooltipRect.height - padding;
    }

    this.renderer.setStyle(tooltipElement, 'top', `${top}px`);
    this.renderer.setStyle(tooltipElement, 'left', `${left}px`);
    this.renderer.setStyle(tooltipElement, 'visibility', 'visible');
    this.renderer.setStyle(tooltipElement, 'opacity', '1');
    this.renderer.setStyle(tooltipElement, 'display', 'block');
    this.renderer.setStyle(tooltipElement, 'position', 'fixed');
    this.renderer.setStyle(tooltipElement, 'z-index', String(this.zIndex.TOOLTIP));

    // Update the data-position attribute for arrow styling
    const tooltipInner = tooltipElement.querySelector('.tour-guide-tooltip');
    if (tooltipInner) {
      this.renderer.setAttribute(tooltipInner, 'data-position', TooltipPosition[finalPosition].toLowerCase());
    }
  }

  private hideTooltip(): void {
    if (!this.isTooltipVisible) return;
    this.isTooltipVisible = false;

    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }

    if (this.overlayElement) {
      this.renderer.removeChild(document.body, this.overlayElement);
      this.overlayElement = null;
    }

    const element = this.elementRef.nativeElement;
    this.renderer.removeClass(element, 'tour-guide-highlighted');
    this.renderer.removeStyle(element, 'z-index');

    if (this.tooltipComponentRef) {
      this.appRef.detachView(this.tooltipComponentRef.hostView);
      this.tooltipComponentRef.destroy();
      this.tooltipComponentRef = null;
    }
  }

  ngOnDestroy(): void {
    this.hideTooltip();
    if (this.scrollListener) {
      this.scrollListener();
      this.scrollListener = null;
    }
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.removeQuestionMarkIcon();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
